import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { superAdminAPI } from '../../services/api';
import { ExportMenu, FilterSelect, ResetFiltersButton } from '../../components/SuperAdmin/ListTools';
import { matchesSearch, formatExportDate } from '../../utils/exportData';
import {
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlinePlus,
  HiOutlineX,
  HiCheck,
  HiStar,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineShieldCheck,
  HiOutlineCog,
  HiOutlinePhone,
  HiOutlineChartBar,
  HiOutlineGlobe,
  HiOutlineDatabase,
  HiOutlineSparkles,
} from 'react-icons/hi';

// Feature categories for organized display
const FEATURE_CATEGORIES = [
  {
    label: 'Calling & Communication',
    icon: HiOutlinePhone,
    features: ['callRecording', 'whatsappIntegration', 'mobileApp'],
  },
  {
    label: 'Analytics & Insights',
    icon: HiOutlineChartBar,
    features: ['analytics', 'advancedAnalytics', 'auditLogs'],
  },
  {
    label: 'Integrations & API',
    icon: HiOutlineGlobe,
    features: ['api', 'webhooks', 'emailIntegration', 'googleSheetIntegration', 'exportData'],
  },
  {
    label: 'Customization & Support',
    icon: HiOutlineCog,
    features: ['customBranding', 'prioritySupport', 'sso'],
  },
];

const FORMAT_MAP = {
  callRecording: 'Call Recording',
  analytics: 'Basic Analytics',
  advancedAnalytics: 'Advanced Analytics',
  api: 'API Access',
  webhooks: 'Webhooks',
  customBranding: 'Custom Branding',
  prioritySupport: 'Priority Support',
  sso: 'SSO / Single Sign-On',
  auditLogs: 'Audit Logs',
  whatsappIntegration: 'WhatsApp Integration',
  emailIntegration: 'Email Integration',
  mobileApp: 'Mobile App',
  exportData: 'Export Data',
  googleSheetIntegration: 'Google Sheets Sync',
};

const LIMIT_LABELS = {
  users: { label: 'Users', placeholder: '5', hint: 'Max users allowed' },
  callsPerMonth: { label: 'Calls / Month', placeholder: '500', hint: '-1 for unlimited' },
  storageMB: { label: 'Storage (MB)', placeholder: '250', hint: '-1 for unlimited' },
  campaigns: { label: 'Campaigns', placeholder: '5', hint: '-1 for unlimited' },
  contacts: { label: 'Contacts / Leads', placeholder: '1000', hint: '-1 for unlimited' },
};

const defaultFormData = {
  name: '',
  slug: '',
  description: '',
  price: { monthly: 0, yearly: 0, currency: 'INR' },
  pricePerUser: { monthly: 0, yearly: 0, currency: 'INR' },
  isPerUser: false,
  gstConfig: 'applicable',
  limits: { users: -1, callsPerMonth: -1, storageMB: -1, campaigns: -1, contacts: -1 },
  features: {
    callRecording: true,
    analytics: true,
    advancedAnalytics: true,
    api: true,
    webhooks: true,
    customBranding: true,
    prioritySupport: true,
    sso: true,
    auditLogs: true,
    whatsappIntegration: true,
    emailIntegration: true,
    mobileApp: true,
    exportData: true,
    googleSheetIntegration: true,
  },
  isPopular: false,
  isActive: true,
  displayOrder: 0,
  trialDays: 5,
};

const inr = (n) => `₹${n.toLocaleString('en-IN')}`;

// Same plan kinds as OrganizationsList / backend computeSubscriptionDates
const planKind = (p) =>
  (p.isPerUser || p.price?.monthly > 0 || p.price?.yearly > 0) ? 'paid' : p.trialDays > 0 ? 'trial' : 'free';

const EMPTY_PLAN_FILTERS = { search: '', status: '', kind: '', pricing: '', gst: '' };

const limitCell = (v) => (v === -1 || v === undefined || v === null ? 'Unlimited' : v);

const PLAN_EXPORT_COLUMNS = [
  { header: 'Name', value: (p) => p.name },
  { header: 'Slug', value: (p) => p.slug },
  { header: 'Description', value: (p) => p.description },
  { header: 'Type', value: (p) => planKind(p) },
  { header: 'Pricing Model', value: (p) => (p.isPerUser ? 'Per-user' : 'Flat') },
  { header: 'Monthly Price (₹)', value: (p) => (p.isPerUser ? p.pricePerUser?.monthly : p.price?.monthly) || 0 },
  { header: 'Yearly Price (₹)', value: (p) => (p.isPerUser ? p.pricePerUser?.yearly : p.price?.yearly) || 0 },
  { header: 'Currency', value: (p) => p.price?.currency || 'INR' },
  { header: 'Trial Days', value: (p) => p.trialDays || 0 },
  { header: 'GST', value: (p) => (p.gstConfig === 'none' ? 'No GST' : 'Applicable') },
  ...Object.entries(LIMIT_LABELS).map(([key, { label }]) => ({ header: label, value: (p) => limitCell(p.limits?.[key]) })),
  ...Object.entries(FORMAT_MAP).map(([key, label]) => ({ header: label, value: (p) => !!p.features?.[key] })),
  { header: 'Popular', value: (p) => !!p.isPopular },
  { header: 'Status', value: (p) => (p.isActive === false ? 'Inactive' : 'Active') },
  { header: 'Display Order', value: (p) => p.displayOrder ?? '' },
  { header: 'Created', value: (p) => formatExportDate(p.createdAt) },
];

const PlansList = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({ ...defaultFormData });
  const [formError, setFormError] = useState('');
  const [activeTab, setActiveTab] = useState('basic'); // basic | pricing | limits | features

  const [planFilters, setPlanFilters] = useState(EMPTY_PLAN_FILTERS);
  const setPlanFilter = (patch) => setPlanFilters((prev) => ({ ...prev, ...patch }));

  const { data: plansData, isLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: async () => {
      const result = await superAdminAPI.getPlans();
      return result;
    },
  });

  // Active plans first (in display order), inactive plans below them
  const allPlans = [...(plansData?.data?.data || [])].sort(
    (a, b) =>
      (a.isActive === false) - (b.isActive === false) ||
      (a.displayOrder ?? 99) - (b.displayOrder ?? 99) ||
      String(a.name).localeCompare(String(b.name))
  );
  // Plans are few and loaded in full, so filtering happens on the client
  const plans = allPlans.filter((p) =>
    matchesSearch(planFilters.search, p.name, p.slug, p.description)
    && (!planFilters.status || (planFilters.status === 'active' ? p.isActive !== false : p.isActive === false))
    && (!planFilters.kind || planKind(p) === planFilters.kind)
    && (!planFilters.pricing || (planFilters.pricing === 'per_user' ? !!p.isPerUser : !p.isPerUser))
    && (!planFilters.gst || (planFilters.gst === 'none' ? p.gstConfig === 'none' : p.gstConfig !== 'none')));
  const firstInactiveId = plans.find((p) => p.isActive === false)?._id;
  const inactiveCount = plans.filter((p) => p.isActive === false).length;

  const createMutation = useMutation({
    mutationFn: superAdminAPI.createPlan,
    onSuccess: () => {
      queryClient.invalidateQueries(['plans']);
      closeModal();
    },
    onError: (error) => setFormError(error.response?.data?.message || 'Failed to create plan'),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await superAdminAPI.updatePlan(id, data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['plans']);
      closeModal();
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.message || error.message || 'Failed to update plan';
      setFormError(errorMsg);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: superAdminAPI.deletePlan,
    onSuccess: () => queryClient.invalidateQueries(['plans']),
  });

  const closeModal = () => {
    setShowModal(false);
    setEditingPlan(null);
    setFormError('');
    setActiveTab('basic');
    setFormData({ ...defaultFormData, features: { ...defaultFormData.features }, limits: { ...defaultFormData.limits }, price: { ...defaultFormData.price }, pricePerUser: { ...defaultFormData.pricePerUser } });
  };

  const openEditModal = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name || '',
      slug: plan.slug || '',
      description: plan.description || '',
      price: {
        monthly: plan.price?.monthly ?? 0,
        yearly: plan.price?.yearly ?? 0,
        currency: plan.price?.currency || 'INR',
      },
      pricePerUser: {
        monthly: plan.pricePerUser?.monthly ?? 0,
        yearly: plan.pricePerUser?.yearly ?? 0,
        currency: plan.pricePerUser?.currency || 'INR',
      },
      isPerUser: plan.isPerUser || false,
      gstConfig: plan.gstConfig || 'applicable',
      limits: plan.limits || { ...defaultFormData.limits },
      features: plan.features || { ...defaultFormData.features },
      isPopular: plan.isPopular || false,
      isActive: plan.isActive !== undefined ? plan.isActive : true,
      displayOrder: plan.displayOrder ?? 0,
      trialDays: plan.trialDays ?? 5,
    });
    setActiveTab('basic');
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith('price.')) {
      const field = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        price: { ...prev.price, [field]: type === 'number' ? parseFloat(value) || 0 : value },
      }));
    } else if (name.startsWith('pricePerUser.')) {
      const field = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        pricePerUser: { ...prev.pricePerUser, [field]: type === 'number' ? parseFloat(value) || 0 : value },
      }));
    } else if (name.startsWith('limits.')) {
      const field = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        limits: { ...prev.limits, [field]: parseInt(value) || 0 },
      }));
    } else if (name.startsWith('features.')) {
      const field = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        features: { ...prev.features, [field]: checked },
      }));
    } else if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData((prev) => ({ ...prev, [name]: parseInt(value) || 0 }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (name === 'name' && !editingPlan) {
        const slug = value
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
        setFormData((prev) => ({ ...prev, slug }));
      }
    }
  };

  const toggleLimitUnlimited = (field) => {
    setFormData((prev) => {
      const isCurrentlyUnlimited = prev.limits[field] === -1;
      return {
        ...prev,
        limits: {
          ...prev.limits,
          [field]: isCurrentlyUnlimited
            ? defaultFormData.limits[field] || 0
            : -1,
        },
      };
    });
  };

  const toggleAllFeatures = (checked) => {
    setFormData((prev) => ({
      ...prev,
      features: Object.fromEntries(Object.keys(prev.features).map((k) => [k, checked])),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.name.trim()) {
      setFormError('Plan name is required');
      setActiveTab('basic');
      return;
    }
    if (!formData.slug.trim()) {
      setFormError('Slug is required');
      setActiveTab('basic');
      return;
    }
    if (formData.price.monthly < 0) {
      setFormError('Monthly price cannot be negative');
      setActiveTab('pricing');
      return;
    }
    if (formData.pricePerUser.monthly < 0) {
      setFormError('Per-user monthly price cannot be negative');
      setActiveTab('pricing');
      return;
    }
    if (formData.pricePerUser.yearly < 0) {
      setFormError('Per-user yearly price cannot be negative');
      setActiveTab('pricing');
      return;
    }
    if (formData.price.yearly < 0) {
      setFormError('Yearly price cannot be negative');
      setActiveTab('pricing');
      return;
    }
    if (formData.trialDays < 0) {
      setFormError('Trial days cannot be negative');
      return;
    }

    const payload = {
      ...formData,
      name: formData.name.trim(),
      slug: formData.slug.trim().toLowerCase(),
    };

    if (editingPlan) {
      updateMutation.mutate({ id: editingPlan._id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDelete = (planId, planName) => {
    if (window.confirm(`Are you sure you want to delete "${planName}"? This cannot be undone.`)) {
      deleteMutation.mutate(planId);
    }
  };

  // Auto-calculate savings when pricing changes
  const monthlyPrice = formData.price.monthly || 0;
  const yearlyPrice = formData.price.yearly || 0;
  const yearlySave = monthlyPrice * 12 - yearlyPrice;
  const perUserMonthly = formData.pricePerUser.monthly || 0;
  const perUserYearly = formData.pricePerUser.yearly || 0;

  const tabs = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'limits', label: 'Limits' },
    { id: 'features', label: 'Features' },
  ];

  return (
    <div className="plans-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <h1>Subscription Plans</h1>
          <p>Manage plans, pricing, and feature access</p>
        </div>
        <div className="header-right">
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <HiOutlinePlus className="w-4 h-4" />
            Create Plan
          </button>
        </div>
      </div>

      {allPlans.length > 0 && (
        <div className="filters-bar">
          <input
            type="text"
            placeholder="Search plan name, slug, description..."
            value={planFilters.search}
            onChange={(e) => setPlanFilter({ search: e.target.value })}
            className="search-input"
          />
          <FilterSelect value={planFilters.status} onChange={(v) => setPlanFilter({ status: v })}
            allLabel="All Status" options={[['active', 'Active'], ['inactive', 'Inactive']]} />
          <FilterSelect value={planFilters.kind} onChange={(v) => setPlanFilter({ kind: v })}
            allLabel="All Plan Types" options={[['free', 'Free'], ['trial', 'Trial'], ['paid', 'Paid']]} />
          <FilterSelect value={planFilters.pricing} onChange={(v) => setPlanFilter({ pricing: v })}
            allLabel="All Pricing Models" options={[['per_user', 'Per-user'], ['flat', 'Flat price']]} />
          <FilterSelect value={planFilters.gst} onChange={(v) => setPlanFilter({ gst: v })}
            allLabel="GST: Any" options={[['applicable', 'GST applicable'], ['none', 'No GST']]} />
          <ResetFiltersButton
            active={Object.keys(EMPTY_PLAN_FILTERS).some((k) => planFilters[k])}
            onReset={() => setPlanFilters(EMPTY_PLAN_FILTERS)}
          />
          <ExportMenu fileName="plans" columns={PLAN_EXPORT_COLUMNS} getRows={() => plans} />
        </div>
      )}

      {/* Plan Cards */}
      {isLoading ? (
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Loading plans...</p>
        </div>
      ) : allPlans.length > 0 && plans.length === 0 ? (
        <div className="empty-state">
          <h3>No plans match these filters</h3>
          <p>Try a different search or reset the filters</p>
        </div>
      ) : plans.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <HiOutlineDatabase className="w-12 h-12 text-gray-300" />
          </div>
          <h3>No plans created</h3>
          <p>Create your first subscription plan to get started</p>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <HiOutlinePlus className="w-4 h-4" />
            Create Plan
          </button>
        </div>
      ) : (
        <div className="plans-grid">
          {plans.map((plan) => {
            const mPrice = plan.price?.monthly || 0;
            const yPrice = plan.price?.yearly || 0;
            const isFree = mPrice === 0 && yPrice === 0;
            const isPerUser = plan.isPerUser;
            const perUserMonthly = plan.pricePerUser?.monthly || 0;
            const perUserYearly = plan.pricePerUser?.yearly || 0;

            return (
              <React.Fragment key={plan._id}>
              {plan._id === firstInactiveId && (
                <div
                  style={{
                    gridColumn: '1 / -1',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginTop: '0.5rem',
                    color: '#6b7280',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                  }}
                >
                  <span>Inactive plans ({inactiveCount})</span>
                  <span style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
                </div>
              )}
              <div
                className={`plan-card ${!plan.isActive ? 'plan-card--inactive' : ''} ${plan.isPopular ? 'plan-card--popular' : ''}`}
              >
                {/* Header */}
                <div className="plan-card__header">
                  {/* Badges — in normal flow above the title so they never overlap the plan name */}
                  {(plan.isPopular || !plan.isActive || isPerUser) && (
                    <div className="plan-card__badges">
                      {!plan.isActive && <span className="plan-card__badge plan-card__badge--inactive">Inactive</span>}
                      {isPerUser && <span className="plan-card__badge plan-card__badge--peruser">Per-User</span>}
                      {plan.isPopular && <span className="plan-card__badge plan-card__badge--popular">★ Popular</span>}
                    </div>
                  )}
                  <div className="plan-card__title-row">
                    <h3 className="plan-card__name">{plan.name}</h3>
                    <span className="plan-card__slug">/{plan.slug}</span>
                  </div>
                  <p className="plan-card__desc">{plan.description || 'No description'}</p>
                </div>

                {/* Pricing */}
                <div className="plan-card__pricing">
                  {isFree ? (
                    <div className="plan-card__price-free">
                      <span className="plan-card__price-amount">Free</span>
                      <span className="plan-card__price-period">forever</span>
                    </div>
                  ) : isPerUser ? (
                    <>
                      <div className="plan-card__price-row plan-card__price-row--main">
                        <span className="plan-card__price-amount">{inr(perUserMonthly)}</span>
                        <span className="plan-card__price-period">/user/mo</span>
                      </div>
                      {perUserYearly > 0 && (
                        <div className="plan-card__price-row plan-card__price-row--alt">
                          <span>
                            {inr(perUserYearly)}/user/mo (yearly)
                            {perUserMonthly > perUserYearly && (
                              <span className="plan-card__save-tag">
                                Save {Math.round((1 - perUserYearly/perUserMonthly)*100)}%
                              </span>
                            )}
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="plan-card__price-row plan-card__price-row--main">
                        <span className="plan-card__price-amount">{inr(mPrice)}</span>
                        <span className="plan-card__price-period">/month</span>
                      </div>
                      {yPrice > 0 && (
                        <div className="plan-card__price-row plan-card__price-row--alt">
                          <span>
                            {inr(yPrice)}/yr
                            {mPrice > 0 && yPrice < mPrice * 12 && (
                              <span className="plan-card__save-tag">
                                Save {inr(mPrice * 12 - yPrice)}
                              </span>
                            )}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                  <div className="plan-card__meta">
                    <span>Trial: {plan.trialDays} days</span>
                    <span>Order: {plan.displayOrder ?? 0}</span>
                    {plan.gstConfig === 'none' && <span style={{ color: '#059669', fontWeight: 600 }}>No GST</span>}
                    {plan.gstConfig === 'applicable' && <span>GST Applicable (state-based)</span>}
                    {!plan.gstConfig && <span>GST Applicable (state-based)</span>}
                  </div>
                </div>

                {/* Limits */}
                <div className="plan-card__section">
                  <h4>Limits</h4>
                  <ul className="plan-card__limits">
                    {Object.entries(LIMIT_LABELS).map(([key, { label }]) => {
                      const val = plan.limits?.[key];
                      return (
                        <li key={key}>
                          <span className="plan-card__label">{label}</span>
                          <span className="plan-card__value">
                            {val === -1 ? '∞ Unlimited' : val?.toLocaleString() || 0}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {/* Features */}
                <div className="plan-card__section plan-card__section--features">
                  <h4>Features</h4>
                  <ul className="plan-card__features">
                    {Object.entries(plan.features || {})
                      .filter(([, enabled]) => enabled)
                      .slice(0, 5)
                      .map(([feature]) => (
                        <li key={feature}>
                          <HiCheck className="w-3.5 h-3.5 text-green-500" />
                          {FORMAT_MAP[feature] || feature}
                        </li>
                      ))}
                    {Object.entries(plan.features || {}).filter(([, v]) => v).length > 5 && (
                      <li className="plan-card__more">
                        +{Object.entries(plan.features || {}).filter(([, v]) => v).length - 5} more
                      </li>
                    )}
                  </ul>
                </div>

                {/* Actions */}
                <div className="plan-card__actions">
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => openEditModal(plan)}
                  >
                    <HiOutlinePencil className="w-3.5 h-3.5" />
                    Edit
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(plan._id, plan.name)}
                  >
                    <HiOutlineTrash className="w-3.5 h-3.5" />
                    Delete
                  </button>
                </div>
              </div>
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* ── Create / Edit Modal ── */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="plan-modal" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="plan-modal__header">
              <div>
                <h2>{editingPlan ? 'Edit Plan' : 'Create New Plan'}</h2>
                <p className="plan-modal__subtitle">
                  {editingPlan ? `Modify ${editingPlan.name}` : 'Configure pricing, limits, and features'}
                </p>
              </div>
              <button className="plan-modal__close" onClick={closeModal}>
                <HiOutlineX className="w-5 h-5" />
              </button>
            </div>

            {/* Tab Navigation */}
            <div className="plan-modal__tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`plan-modal__tab ${activeTab === tab.id ? 'plan-modal__tab--active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Form Body */}
            <form onSubmit={handleSubmit} className="plan-modal__body">
              {formError && (
                <div className="plan-modal__error">
                  <HiOutlineX className="w-4 h-4" />
                  {formError}
                </div>
              )}

              {/* ── Tab: Basic Info ── */}
              {activeTab === 'basic' && (
                <div className="plan-modal__section">
                  <div className="plan-modal__grid plan-modal__grid--2col">
                    <div className="plan-modal__field">
                      <label>
                        Plan Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="e.g. Starter Team"
                        required
                      />
                    </div>
                    <div className="plan-modal__field">
                      <label>
                        Slug <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        name="slug"
                        value={formData.slug}
                        onChange={handleInputChange}
                        placeholder="e.g. starter-team"
                        required
                        disabled={!!editingPlan}
                        className={editingPlan ? 'field-disabled' : ''}
                      />
                      {editingPlan && <span className="field-hint">Slug cannot be changed after creation</span>}
                    </div>
                  </div>

                  <div className="plan-modal__field">
                    <label>Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Brief description of the plan and who it's for"
                      rows={2}
                    />
                  </div>

                  <div className="plan-modal__grid plan-modal__grid--3col">
                    <div className="plan-modal__field">
                      <label>Trial Days</label>
                      <input
                        type="number"
                        name="trialDays"
                        value={formData.trialDays}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="14"
                      />
                      <span className="field-hint">0 = no trial (Free plan)</span>
                    </div>
                    <div className="plan-modal__field">
                      <label>Display Order</label>
                      <input
                        type="number"
                        name="displayOrder"
                        value={formData.displayOrder}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="0"
                      />
                      <span className="field-hint">Lower = shown first</span>
                    </div>
                    <div className="plan-modal__field">
                      <label>Active Status</label>
                      <div className="plan-modal__toggle-row">
                        <button
                          type="button"
                          className={`toggle-btn ${formData.isActive ? 'toggle-btn--active' : 'toggle-btn--inactive'}`}
                          onClick={() => setFormData((prev) => ({ ...prev, isActive: !prev.isActive }))}
                        >
                          {formData.isActive ? (
                            <><HiOutlineEye className="w-4 h-4" /> Active</>
                          ) : (
                            <><HiOutlineEyeOff className="w-4 h-4" /> Inactive</>
                          )}
                        </button>
                      </div>
                      <span className="field-hint">Inactive plans are hidden from users</span>
                    </div>
                  </div>

                  <div className="plan-modal__field">
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        name="isPopular"
                        checked={formData.isPopular}
                        onChange={handleInputChange}
                      />
                      <HiStar className="w-4 h-4 text-amber-500" />
                      Mark as Popular / Recommended Plan
                    </label>
                  </div>
                </div>
              )}

              {/* ── Tab: Pricing ── */}
              {activeTab === 'pricing' && (
                <div className="plan-modal__section">
                  <div className="plan-modal__pricing-intro">
                    <span className="pricing-intro-icon" style={{ fontSize: '1.25rem' }}>₹</span>
                    <div>
                      <h4>Pricing Configuration</h4>
                      <p>Set prices for each billing cycle. Use ₹0 for all fields to make this a Free plan.</p>
                    </div>
                  </div>

                  {/* Per-User Pricing Toggle */}
                  <div className="plan-modal__field" style={{ marginBottom: '1rem' }}>
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        name="isPerUser"
                        checked={formData.isPerUser}
                        onChange={handleInputChange}
                      />
                      <HiOutlineSparkles className="w-4 h-4 text-purple-500" />
                      Per-User Pricing (price × number of users)
                    </label>
                    {formData.isPerUser && (
                      <span className="field-hint" style={{ marginTop: '0.25rem', display: 'block' }}>
                        When enabled, the total price = per-user rate × number of users. Users limit should be set to -1 (unlimited) and enforced by paid seats.
                      </span>
                    )}
                  </div>

                  {formData.isPerUser ? (
                    <>
                      {/* Per-User Pricing */}
                      <div className="plan-modal__pricing-intro" style={{ marginBottom: '1rem' }}>
                        <HiOutlineSparkles className="w-5 h-5 text-purple-500" />
                        <div>
                          <h4>Per-User Pricing</h4>
                          <p>Set the price per user for each billing cycle. Total = rate × users.</p>
                        </div>
                      </div>
                      <div className="plan-modal__pricing-grid">
                        {/* Per-User Monthly */}
                        <div className="plan-modal__pricing-card">
                          <div className="pricing-card__header">
                            <span className="pricing-card__cycle">Monthly</span>
                            <span className="pricing-card__badge">Per user</span>
                          </div>
                          <div className="pricing-card__input">
                            <span className="pricing-card__currency">₹</span>
                            <input
                              type="number"
                              name="pricePerUser.monthly"
                              value={formData.pricePerUser.monthly}
                              onChange={handleInputChange}
                              min="0"
                              step="1"
                              placeholder="0"
                            />
                            <span className="pricing-card__unit">/user/mo</span>
                          </div>
                        </div>

                        {/* Per-User Yearly */}
                        <div className="plan-modal__pricing-card">
                          <div className="pricing-card__header">
                            <span className="pricing-card__cycle">Yearly</span>
                            <span className="pricing-card__badge">Per user/month</span>
                          </div>
                          <div className="pricing-card__input">
                            <span className="pricing-card__currency">₹</span>
                            <input
                              type="number"
                              name="pricePerUser.yearly"
                              value={formData.pricePerUser.yearly}
                              onChange={handleInputChange}
                              min="0"
                              step="1"
                              placeholder="0"
                            />
                            <span className="pricing-card__unit">/user/mo</span>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Flat Pricing */}
                      <div className="plan-modal__pricing-grid">
                        {/* Monthly */}
                        <div className="plan-modal__pricing-card">
                          <div className="pricing-card__header">
                            <span className="pricing-card__cycle">Monthly</span>
                            <span className="pricing-card__badge">Billed monthly</span>
                          </div>
                          <div className="pricing-card__input">
                            <span className="pricing-card__currency">₹</span>
                            <input
                              type="number"
                              name="price.monthly"
                              value={formData.price.monthly}
                              onChange={handleInputChange}
                              min="0"
                              step="1"
                              placeholder="0"
                            />
                            <span className="pricing-card__unit">/mo</span>
                          </div>
                        </div>

                        {/* Yearly */}
                        <div className="plan-modal__pricing-card">
                          <div className="pricing-card__header">
                            <span className="pricing-card__cycle">Yearly</span>
                            {yearlySave > 0 && (
                              <span className="pricing-card__badge pricing-card__badge--save">
                                Save {inr(yearlySave)}
                              </span>
                            )}
                          </div>
                          <div className="pricing-card__input">
                            <span className="pricing-card__currency">₹</span>
                            <input
                              type="number"
                              name="price.yearly"
                              value={formData.price.yearly}
                              onChange={handleInputChange}
                              min="0"
                              step="1"
                              placeholder="0"
                            />
                            <span className="pricing-card__unit">/yr</span>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Quick pricing preview */}
                  {(formData.price.monthly > 0 || formData.price.yearly > 0 || formData.pricePerUser.monthly > 0 || formData.pricePerUser.yearly > 0) && (
                    <div className="plan-modal__pricing-preview">
                      <h4>Pricing Preview</h4>
                      <div className="pricing-preview__grid">
                        {formData.isPerUser ? (
                          <>
                            <div className="pricing-preview__item">
                              <span className="pricing-preview__label">Monthly (per user)</span>
                              <span className="pricing-preview__value">{inr(formData.pricePerUser.monthly)}/user/mo</span>
                            </div>
                            <div className="pricing-preview__item">
                              <span className="pricing-preview__label">Yearly (per user/month)</span>
                              <span className="pricing-preview__value">{inr(formData.pricePerUser.yearly)}/user/mo</span>
                            </div>
                            <div className="pricing-preview__item">
                              <span className="pricing-preview__label">Yearly total (1 user)</span>
                              <span className="pricing-preview__value">{inr(formData.pricePerUser.yearly * 12)}/yr</span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="pricing-preview__item">
                              <span className="pricing-preview__label">Monthly</span>
                              <span className="pricing-preview__value">{inr(formData.price.monthly)}/mo</span>
                            </div>
                            {formData.price.yearly > 0 && (
                              <div className="pricing-preview__item">
                                <span className="pricing-preview__label">Yearly</span>
                                <span className="pricing-preview__value">{inr(formData.price.yearly)} total</span>
                                {yearlySave > 0 && (
                                  <span className="pricing-preview__save">≈ {inr(Math.round(formData.price.yearly / 12))}/mo</span>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="plan-modal__field" style={{ marginTop: '0.5rem' }}>
                    <label>Currency</label>
                    <select name="price.currency" value={formData.price.currency} onChange={handleInputChange}>
                      <option value="INR">INR (₹)</option>
                      <option value="USD">USD ($)</option>
                    </select>
                  </div>

                  {/* GST Configuration */}
                  <div className="plan-modal__field" style={{ marginTop: '0.5rem' }}>
                    <label>
                      GST Configuration
                      <span className="field-hint" style={{ display: 'block', marginTop: '0.125rem' }}>
                        How GST is applied to this plan's pricing
                      </span>
                    </label>
                    <select name="gstConfig" value={formData.gstConfig} onChange={handleInputChange}>
                      <option value="none">No GST — Prices shown are final, no tax added</option>
                      <option value="applicable">GST Applicable — IGST or CGST+SGST based on customer's state</option>
                    </select>
                    {formData.gstConfig === 'none' && (
                      <span className="field-hint" style={{ color: '#059669' }}>
                        ✔ No GST will be applied. Total = Base price.
                      </span>
                    )}
                    {formData.gstConfig === 'applicable' && (
                      <span className="field-hint" style={{ color: '#6366f1' }}>
                        ✔ GST is applied based on the customer's state: CGST 9% + SGST 9% if in Maharashtra, IGST 18% otherwise.
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* ── Tab: Limits ── */}
              {activeTab === 'limits' && (
                <div className="plan-modal__section">
                  <div className="plan-modal__pricing-intro">
                    <HiOutlineShieldCheck className="w-5 h-5 text-blue-500" />
                    <div>
                      <h4>Resource Limits</h4>
                      <p>Set usage caps for each resource. Use -1 for unlimited, or toggle the ∞ button next to each field.</p>
                    </div>
                  </div>

                  <div className="plan-modal__limits-grid">
                    {Object.entries(LIMIT_LABELS).map(([key, { label, placeholder, hint }]) => {
                      const isUnlimited = formData.limits[key] === -1;
                      return (
                        <div key={key} className={`plan-modal__limit-item ${isUnlimited ? 'plan-modal__limit-item--unlimited' : ''}`}>
                          <label>{label}</label>
                          <div className="limit-input-row">
                            {isUnlimited ? (
                              <div className="limit-unlimited-display">∞ Unlimited</div>
                            ) : (
                              <input
                                type="number"
                                name={`limits.${key}`}
                                value={formData.limits[key]}
                                onChange={handleInputChange}
                                min={key === 'users' ? '1' : '0'}
                                placeholder={placeholder}
                              />
                            )}
                            <button
                              type="button"
                              className={`limit-toggle ${isUnlimited ? 'limit-toggle--active' : ''}`}
                              onClick={() => toggleLimitUnlimited(key)}
                              title={isUnlimited ? 'Set custom limit' : 'Set unlimited'}
                            >
                              ∞
                            </button>
                          </div>
                          <span className="field-hint">{isUnlimited ? 'No limit enforced' : hint}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Tab: Features ── */}
              {activeTab === 'features' && (
                <div className="plan-modal__section">
                  <div className="plan-modal__pricing-intro">
                    <HiOutlineCog className="w-5 h-5 text-purple-500" />
                    <div>
                      <h4>Feature Access</h4>
                      <p>Control which features are available on this plan.</p>
                    </div>
                  </div>

                  <div className="plan-modal__features-toolbar">
                    <button type="button" className="btn btn-sm btn-outline" onClick={() => toggleAllFeatures(true)}>
                      Enable All
                    </button>
                    <button type="button" className="btn btn-sm btn-outline" onClick={() => toggleAllFeatures(false)}>
                      Disable All
                    </button>
                  </div>

                  <div className="plan-modal__feature-categories">
                    {FEATURE_CATEGORIES.map((cat) => {
                      const CatIcon = cat.icon;
                      const enabledCount = cat.features.filter((f) => formData.features[f]).length;
                      return (
                        <div key={cat.label} className="feature-category">
                          <div className="feature-category__header">
                            <CatIcon className="w-4 h-4 text-gray-400" />
                            <span className="feature-category__title">{cat.label}</span>
                            <span className="feature-category__count">
                              {enabledCount}/{cat.features.length}
                            </span>
                          </div>
                          <div className="feature-category__items">
                            {cat.features.map((feature) => (
                              <label key={feature} className="feature-toggle">
                                <input
                                  type="checkbox"
                                  name={`features.${feature}`}
                                  checked={!!formData.features[feature]}
                                  onChange={handleInputChange}
                                />
                                <span className="feature-toggle__slider" />
                                <span className="feature-toggle__label">{FORMAT_MAP[feature] || feature}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Submit */}
              <div className="plan-modal__actions">
                <button type="button" className="btn btn-outline" onClick={closeModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingPlan
                    ? 'Update Plan'
                    : 'Create Plan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Scoped Styles ── */}
      <style>{`
        /* ===== Plans Grid ===== */
        .plans-page {
          max-width: 100%;
        }

        .plans-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
          gap: 1.25rem;
        }

        /* ===== Plan Card ===== */
        .plan-card {
          background: #fff;
          border-radius: 0.75rem;
          border: 1.5px solid #e5e7eb;
          overflow: hidden;
          position: relative;
          transition: all 0.2s ease;
          display: flex;
          flex-direction: column;
        }
        .plan-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px -5px rgba(0,0,0,0.1);
          border-color: #d1d5db;
        }
        .plan-card--popular {
          border-color: #6366f1;
          border-width: 2px;
        }
        .plan-card--popular:hover {
          border-color: #4f46e5;
        }
        .plan-card--inactive {
          opacity: 0.65;
        }
        .plan-card--inactive:hover {
          opacity: 0.85;
        }

        .plan-card__badges {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.375rem;
          margin-bottom: 0.625rem;
        }
        .plan-card__badge {
          display: inline-block;
          line-height: 1.4;
          padding: 0.2rem 0.65rem;
          border-radius: 9999px;
          font-size: 0.6875rem;
          font-weight: 600;
          letter-spacing: 0.02em;
          z-index: 2;
        }
        .plan-card__badge--popular {
          background: #6366f1;
          color: #fff;
        }
        .plan-card__badge--inactive {
          background: #9ca3af;
          color: #fff;
        }
        .plan-card__badge--peruser {
          background: #8B5CF6;
          color: #fff;
        }

        .plan-card__header {
          padding: 1.25rem 1.25rem 0.75rem;
        }
        .plan-card__title-row {
          display: flex;
          flex-wrap: wrap;
          align-items: baseline;
          gap: 0.375rem;
          margin-bottom: 0.25rem;
        }
        .plan-card__name {
          overflow-wrap: anywhere;
          font-size: 1.125rem;
          font-weight: 700;
          color: #111827;
          margin: 0;
        }
        .plan-card__slug {
          font-size: 0.75rem;
          color: #9ca3af;
          font-family: monospace;
        }
        .plan-card__desc {
          font-size: 0.8125rem;
          color: #6b7280;
          margin: 0;
          line-height: 1.4;
        }

        .plan-card__pricing {
          padding: 1rem 1.25rem;
          background: #f9fafb;
          border-top: 1px solid #f3f4f6;
          border-bottom: 1px solid #f3f4f6;
        }
        .plan-card__price-free {
          display: flex;
          align-items: baseline;
          gap: 0.375rem;
        }
        .plan-card__price-amount {
          font-size: 1.75rem;
          font-weight: 800;
          color: #059669;
        }
        .plan-card__price-period {
          font-size: 0.75rem;
          color: #9ca3af;
        }
        .plan-card__price-row {
          display: flex;
          align-items: baseline;
          gap: 0.25rem;
        }
        .plan-card__price-row--main {
          margin-bottom: 0.25rem;
        }
        .plan-card__price-row--main .plan-card__price-amount {
          font-size: 1.75rem;
          font-weight: 800;
          color: #111827;
        }
        .plan-card__price-row--alt {
          font-size: 0.8125rem;
          color: #6b7280;
          display: flex;
          align-items: center;
          gap: 0.25rem;
          flex-wrap: wrap;
        }
        .plan-card__price-sep {
          color: #d1d5db;
        }
        .plan-card__save-tag {
          display: inline-block;
          background: #ecfdf5;
          color: #059669;
          font-size: 0.6875rem;
          font-weight: 600;
          padding: 0.1rem 0.4rem;
          border-radius: 0.25rem;
          margin-left: 0.25rem;
        }
        .plan-card__meta {
          display: flex;
          gap: 1rem;
          margin-top: 0.5rem;
          font-size: 0.6875rem;
          color: #9ca3af;
        }

        .plan-card__section {
          padding: 0.75rem 1.25rem;
        }
        .plan-card__section--features {
          flex: 1;
        }
        .plan-card__section h4 {
          font-size: 0.6875rem;
          font-weight: 600;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin: 0 0 0.5rem;
        }

        .plan-card__limits {
          list-style: none;
          padding: 0;
          margin: 0;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.25rem 1rem;
        }
        .plan-card__limits li {
          display: flex;
          justify-content: space-between;
          font-size: 0.8125rem;
          padding: 0.2rem 0;
        }
        .plan-card__label {
          color: #6b7280;
        }
        .plan-card__value {
          font-weight: 600;
          color: #111827;
        }

        .plan-card__features {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .plan-card__features li {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          font-size: 0.8125rem;
          color: #374151;
          padding: 0.2rem 0;
        }
        .plan-card__more {
          color: #9ca3af !important;
          font-style: italic;
          font-size: 0.75rem !important;
        }

        .plan-card__actions {
          padding: 0.75rem 1.25rem;
          border-top: 1px solid #f3f4f6;
          display: flex;
          gap: 0.5rem;
        }

        /* ===== Loading & Empty ===== */
        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          padding: 3rem;
          color: #6b7280;
        }
        .loading-spinner {
          width: 2rem;
          height: 2rem;
          border: 3px solid #e5e7eb;
          border-top-color: #6366f1;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* ===== Plan Modal ===== */
        .plan-modal {
          background: #fff;
          border-radius: 1rem;
          box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
          width: 100%;
          max-width: 720px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          margin: auto;
        }

        .plan-modal__header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid #e5e7eb;
          background: #f9fafb;
        }
        .plan-modal__header h2 {
          font-size: 1.125rem;
          font-weight: 700;
          color: #111827;
          margin: 0;
        }
        .plan-modal__subtitle {
          font-size: 0.8125rem;
          color: #6b7280;
          margin: 0.25rem 0 0;
        }
        .plan-modal__close {
          background: transparent;
          border: none;
          padding: 0.375rem;
          border-radius: 0.5rem;
          color: #9ca3af;
          cursor: pointer;
          transition: all 0.15s;
        }
        .plan-modal__close:hover {
          background: #e5e7eb;
          color: #374151;
        }

        .plan-modal__tabs {
          display: flex;
          border-bottom: 1px solid #e5e7eb;
          padding: 0 1.5rem;
          background: #f9fafb;
        }
        .plan-modal__tab {
          padding: 0.75rem 1.25rem;
          font-size: 0.8125rem;
          font-weight: 500;
          color: #6b7280;
          background: transparent;
          border: none;
          border-bottom: 2px solid transparent;
          cursor: pointer;
          transition: all 0.15s;
        }
        .plan-modal__tab:hover {
          color: #374151;
        }
        .plan-modal__tab--active {
          color: #6366f1;
          border-bottom-color: #6366f1;
          font-weight: 600;
        }

        .plan-modal__body {
          padding: 1.5rem;
          overflow-y: auto;
          flex: 1;
        }

        .plan-modal__error {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: #fef2f2;
          color: #991b1b;
          padding: 0.75rem 1rem;
          border-radius: 0.5rem;
          margin-bottom: 1rem;
          font-size: 0.875rem;
          border: 1px solid #fecaca;
        }

        .plan-modal__section {
          animation: fadeIn 0.2s ease;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .plan-modal__grid--2col {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }
        .plan-modal__grid--3col {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 1rem;
        }
        @media (max-width: 640px) {
          .plan-modal__grid--2col,
          .plan-modal__grid--3col {
            grid-template-columns: 1fr;
          }
        }

        .plan-modal__field {
          margin-bottom: 1rem;
        }
        .plan-modal__field label {
          display: block;
          font-size: 0.8125rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.375rem;
        }
        .plan-modal__field .required {
          color: #ef4444;
        }
        .plan-modal__field input,
        .plan-modal__field textarea,
        .plan-modal__field select {
          width: 100%;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          border: 1.5px solid #d1d5db;
          border-radius: 0.5rem;
          background: #fff;
          transition: all 0.15s;
          color: #111827;
        }
        .plan-modal__field input:focus,
        .plan-modal__field textarea:focus,
        .plan-modal__field select:focus {
          outline: none;
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
        }
        .plan-modal__field input.field-disabled {
          background: #f3f4f6;
          color: #9ca3af;
          cursor: not-allowed;
        }
        .plan-modal__field textarea {
          resize: vertical;
          min-height: 60px;
        }
        .field-hint {
          display: block;
          font-size: 0.6875rem;
          color: #9ca3af;
          margin-top: 0.25rem;
        }

        /* Toggle button for active/inactive */
        .plan-modal__toggle-row {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .toggle-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.375rem 0.75rem;
          border-radius: 0.5rem;
          font-size: 0.8125rem;
          font-weight: 600;
          cursor: pointer;
          border: 1.5px solid;
          transition: all 0.15s;
        }
        .toggle-btn--active {
          background: #ecfdf5;
          border-color: #6ee7b7;
          color: #059669;
        }
        .toggle-btn--active:hover {
          background: #d1fae5;
        }
        .toggle-btn--inactive {
          background: #fef2f2;
          border-color: #fecaca;
          color: #dc2626;
        }
        .toggle-btn--inactive:hover {
          background: #fee2e2;
        }

        .toggle-label {
          display: flex !important;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: #374151;
          cursor: pointer;
          padding: 0.75rem 1rem;
          border-radius: 0.5rem;
          border: 1.5px solid #e5e7eb;
          transition: all 0.15s;
        }
        .toggle-label:hover {
          background: #f9fafb;
          border-color: #d1d5db;
        }
        .toggle-label input[type="checkbox"] {
          width: 1rem;
          height: 1rem;
          accent-color: #6366f1;
        }

        /* Pricing cards */
        .plan-modal__pricing-intro {
          display: flex;
          gap: 0.75rem;
          padding: 1rem;
          background: #f9fafb;
          border-radius: 0.75rem;
          margin-bottom: 1.25rem;
        }
        .pricing-intro-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 2rem;
          height: 2rem;
          border-radius: 0.5rem;
          background: #ecfdf5;
          color: #059669;
          font-weight: 700;
          font-size: 1rem;
          flex-shrink: 0;
        }
        .plan-modal__pricing-intro h4 {
          font-size: 0.875rem;
          font-weight: 700;
          color: #111827;
          margin: 0;
        }
        .plan-modal__pricing-intro p {
          font-size: 0.75rem;
          color: #6b7280;
          margin: 0.25rem 0 0;
        }

        .plan-modal__pricing-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
          margin-bottom: 1.25rem;
        }
        @media (max-width: 640px) {
          .plan-modal__pricing-grid {
            grid-template-columns: 1fr;
          }
        }

        .plan-modal__pricing-card {
          background: #fff;
          border: 1.5px solid #e5e7eb;
          border-radius: 0.75rem;
          overflow: hidden;
          transition: border-color 0.15s;
        }
        .plan-modal__pricing-card:focus-within {
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99,102,241,0.08);
        }

        .pricing-card__header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.625rem 0.875rem;
          background: #f9fafb;
          border-bottom: 1px solid #f3f4f6;
        }
        .pricing-card__cycle {
          font-size: 0.8125rem;
          font-weight: 600;
          color: #374151;
        }
        .pricing-card__badge {
          font-size: 0.625rem;
          font-weight: 500;
          color: #9ca3af;
          background: #f3f4f6;
          padding: 0.125rem 0.5rem;
          border-radius: 9999px;
        }
        .pricing-card__badge--save {
          background: #ecfdf5;
          color: #059669;
          font-weight: 600;
        }

        .pricing-card__input {
          display: flex;
          align-items: center;
          padding: 0.75rem 0.875rem;
          gap: 0.25rem;
        }
        .pricing-card__currency {
          font-size: 1rem;
          font-weight: 600;
          color: #6b7280;
        }
        .pricing-card__input input {
          flex: 1;
          width: 100%;
          padding: 0.375rem 0;
          font-size: 1.25rem;
          font-weight: 700;
          color: #111827;
          border: none;
          background: transparent;
          outline: none;
          -moz-appearance: textfield;
        }
        .pricing-card__input input::-webkit-inner-spin-button,
        .pricing-card__input input::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .pricing-card__unit {
          font-size: 0.75rem;
          color: #9ca3af;
          font-weight: 500;
        }

        /* Pricing preview */
        .plan-modal__pricing-preview {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 0.75rem;
          padding: 1rem;
          margin-bottom: 1rem;
        }
        .plan-modal__pricing-preview h4 {
          font-size: 0.75rem;
          font-weight: 600;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin: 0 0 0.75rem;
        }
        .pricing-preview__grid {
          display: flex;
          gap: 1.5rem;
          flex-wrap: wrap;
        }
        .pricing-preview__item {
          display: flex;
          flex-direction: column;
          gap: 0.125rem;
        }
        .pricing-preview__label {
          font-size: 0.6875rem;
          color: #9ca3af;
        }
        .pricing-preview__value {
          font-size: 0.875rem;
          font-weight: 600;
          color: #111827;
        }
        .pricing-preview__save {
          font-size: 0.6875rem;
          color: #059669;
          font-weight: 500;
        }

        /* Limits grid */
        .plan-modal__limits-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.25rem;
        }
        @media (max-width: 640px) {
          .plan-modal__limits-grid {
            grid-template-columns: 1fr;
          }
        }

        .plan-modal__limit-item {
          padding: 1rem;
          border: 1.5px solid #e5e7eb;
          border-radius: 0.75rem;
          background: #fff;
          transition: all 0.15s;
        }
        .plan-modal__limit-item--unlimited {
          border-color: #6ee7b7;
          background: #ecfdf5;
        }
        .plan-modal__limit-item label {
          display: block;
          font-size: 0.8125rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.5rem;
        }

        .limit-input-row {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }
        .limit-input-row input {
          flex: 1;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          border: 1.5px solid #d1d5db;
          border-radius: 0.5rem;
          background: #fff;
          color: #111827;
          transition: all 0.15s;
        }
        .limit-input-row input:focus {
          outline: none;
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
        }

        .limit-unlimited-display {
          flex: 1;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: #059669;
          background: #d1fae5;
          border-radius: 0.5rem;
          text-align: center;
        }

        .limit-toggle {
          width: 2.25rem;
          height: 2.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1.5px solid #d1d5db;
          border-radius: 0.5rem;
          background: #fff;
          font-size: 1rem;
          font-weight: 700;
          color: #9ca3af;
          cursor: pointer;
          transition: all 0.15s;
        }
        .limit-toggle:hover {
          border-color: #6366f1;
          color: #6366f1;
        }
        .limit-toggle--active {
          background: #6366f1;
          border-color: #6366f1;
          color: #fff;
        }
        .limit-toggle--active:hover {
          background: #4f46e5;
          color: #fff;
        }

        /* Features */
        .plan-modal__features-toolbar {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .plan-modal__feature-categories {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }
        @media (max-width: 640px) {
          .plan-modal__feature-categories {
            grid-template-columns: 1fr;
          }
        }

        .feature-category {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 0.75rem;
          overflow: hidden;
        }
        .feature-category__header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          background: #fff;
          border-bottom: 1px solid #e5e7eb;
        }
        .feature-category__title {
          font-size: 0.8125rem;
          font-weight: 600;
          color: #374151;
          flex: 1;
        }
        .feature-category__count {
          font-size: 0.6875rem;
          font-weight: 500;
          color: #9ca3af;
          background: #f3f4f6;
          padding: 0.125rem 0.5rem;
          border-radius: 9999px;
        }

        .feature-category__items {
          padding: 0.5rem 1rem;
        }

        .feature-toggle {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.5rem 0;
          cursor: pointer;
          font-size: 0.8125rem;
          color: #374151;
        }
        .feature-toggle input[type="checkbox"] {
          display: none;
        }
        .feature-toggle__slider {
          width: 2.25rem;
          height: 1.25rem;
          background: #d1d5db;
          border-radius: 9999px;
          position: relative;
          transition: all 0.2s;
          flex-shrink: 0;
        }
        .feature-toggle__slider::after {
          content: '';
          position: absolute;
          top: 0.125rem;
          left: 0.125rem;
          width: 1rem;
          height: 1rem;
          background: #fff;
          border-radius: 50%;
          transition: all 0.2s;
          box-shadow: 0 1px 3px rgba(0,0,0,0.15);
        }
        .feature-toggle input:checked + .feature-toggle__slider {
          background: #6366f1;
        }
        .feature-toggle input:checked + .feature-toggle__slider::after {
          left: calc(100% - 1.125rem);
        }
        .feature-toggle__label {
          user-select: none;
        }

        /* Modal Actions */
        .plan-modal__actions {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1.25rem 1.5rem;
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
        }

        /* Button overrides */
        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.375rem;
          padding: 0.5rem 1rem;
          font-size: 0.875rem;
          font-weight: 500;
          border-radius: 0.5rem;
          border: none;
          cursor: pointer;
          transition: all 0.15s;
          text-decoration: none;
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .btn-primary {
          background: #6366f1;
          color: #fff;
        }
        .btn-primary:hover:not(:disabled) {
          background: #4f46e5;
        }
        .btn-outline {
          background: transparent;
          border: 1.5px solid #d1d5db;
          color: #374151;
        }
        .btn-outline:hover:not(:disabled) {
          background: #f9fafb;
          border-color: #9ca3af;
        }
        .btn-danger {
          background: #ef4444;
          color: #fff;
        }
        .btn-danger:hover:not(:disabled) {
          background: #dc2626;
        }
        .btn-sm {
          padding: 0.375rem 0.75rem;
          font-size: 0.75rem;
        }

        /* ===== Plan cards — same colors as the admin panel Full Access card ===== */
        /* Light: white → light indigo tint gradient, indigo border */
        .plan-card {
          background: linear-gradient(135deg, #ffffff 0%, rgba(238, 242, 255, 0.35) 55%, #ffffff 100%);
          border: 2px solid #818cf8;
        }
        .plan-card:hover {
          border-color: #4f46e5;
        }
        .plan-card--popular {
          border-color: #4f46e5;
        }
        .plan-card--popular:hover {
          border-color: #4338ca;
        }

        /* Dark: indigo radial navy card, same as admin dark Full Access card */
        html.dark .plan-card {
          background: radial-gradient(ellipse at top right, rgba(99, 102, 241, 0.40), rgba(49, 46, 129, 0.92) 52%, rgba(2, 6, 23, 0.98));
          border-color: rgba(139, 92, 246, 0.7);
        }
        html.dark .plan-card:hover {
          border-color: #8b5cf6;
          box-shadow: 0 0 55px -12px rgba(129, 140, 248, 0.45);
        }
        html.dark .plan-card--popular {
          border-color: #818cf8;
        }
        html.dark .plan-card__badge--popular {
          background: linear-gradient(to right, #6366f1, #8b5cf6);
        }
        html.dark .plan-card__badge--inactive {
          background: #273056;
          color: #aebdce;
        }
        html.dark .plan-card__name { color: #f1f5f9; }
        html.dark .plan-card__slug { color: #8ea1b8; }
        html.dark .plan-card__desc { color: #aebdce; }
        html.dark .plan-card__pricing {
          background: rgba(2, 6, 23, 0.35);
          border-top-color: rgba(42, 49, 88, 0.8);
          border-bottom-color: rgba(42, 49, 88, 0.8);
        }
        html.dark .plan-card__price-amount { color: #34d399; }
        html.dark .plan-card__price-row--main .plan-card__price-amount { color: #f1f5f9; }
        html.dark .plan-card__price-period,
        html.dark .plan-card__price-sep,
        html.dark .plan-card__meta,
        html.dark .plan-card__section h4 { color: #8ea1b8; }
        html.dark .plan-card__price-row--alt,
        html.dark .plan-card__label { color: #aebdce; }
        html.dark .plan-card__value { color: #f1f5f9; }
        html.dark .plan-card__features li { color: #cbd5e1; }
        html.dark .plan-card__more { color: #8ea1b8 !important; }
        html.dark .plan-card__save-tag {
          background: rgba(16, 185, 129, 0.18);
          color: #34d399;
        }
        html.dark .plan-card__actions { border-top-color: rgba(42, 49, 88, 0.8); }
        html.dark .plan-modal__close {
          color: #8ea1b8;
        }
        html.dark .plan-modal__close:hover {
          background: #1a2140;
          color: #f1f5f9;
        }

        /* Dark: plan modal (same navy family as the rest of the panel) */
        html.dark .plan-modal {
          background: #161c36;
          border: 1px solid #2a3158;
          box-shadow: none;
        }
        html.dark .plan-modal__header {
          background: #131833;
          border-bottom-color: #2a3158;
        }
        html.dark .plan-modal__header h2 { color: #f1f5f9; }
        html.dark .plan-modal__subtitle { color: #8ea1b8; }
        html.dark .plan-modal__tabs {
          background: #131833;
          border-bottom-color: #2a3158;
        }
        html.dark .plan-modal__tab { color: #8ea1b8; }
        html.dark .plan-modal__tab:hover { color: #cbd5e1; }
        html.dark .plan-modal__tab--active { color: #a5b4fc; border-bottom-color: #818cf8; }
        html.dark .plan-modal__error {
          background: rgba(239, 68, 68, 0.15);
          color: #fca5a5;
          border-color: rgba(248, 113, 113, 0.4);
        }
        html.dark .plan-modal__field label { color: #cbd5e1; }
        html.dark .plan-modal__field input,
        html.dark .plan-modal__field textarea,
        html.dark .plan-modal__field select {
          background: #0c1226;
          border-color: #2a3158;
          color: #e5e7eb;
        }
        html.dark .plan-modal__field input.field-disabled {
          background: #131833;
          color: #64748b;
        }
        html.dark .field-hint { color: #8ea1b8; }

        /* Dark: plan modal — toggle buttons, pricing cards, limits, features, actions */
        html.dark .toggle-btn--active {
          background: rgba(16, 185, 129, 0.18);
          border-color: rgba(52, 211, 153, 0.5);
          color: #34d399;
        }
        html.dark .toggle-btn--active:hover { background: rgba(16, 185, 129, 0.28); }
        html.dark .toggle-btn--inactive {
          background: rgba(239, 68, 68, 0.15);
          border-color: rgba(248, 113, 113, 0.5);
          color: #f87171;
        }
        html.dark .toggle-btn--inactive:hover { background: rgba(239, 68, 68, 0.25); }
        html.dark .toggle-label {
          color: #cbd5e1;
          border-color: #2a3158;
        }
        html.dark .toggle-label:hover { background: #151c38; border-color: #384070; }

        html.dark .plan-modal__pricing-intro {
          background: #131833;
        }
        html.dark .pricing-intro-icon {
          background: rgba(16, 185, 129, 0.18);
          color: #34d399;
        }
        html.dark .plan-modal__pricing-intro h4 { color: #f1f5f9; }
        html.dark .plan-modal__pricing-intro p { color: #8ea1b8; }

        html.dark .plan-modal__pricing-card {
          background: #161c36;
          border-color: #2a3158;
        }
        html.dark .plan-modal__pricing-card:focus-within {
          border-color: #818cf8;
          box-shadow: 0 0 0 3px rgba(129, 140, 248, 0.15);
        }
        html.dark .pricing-card__header {
          background: #131833;
          border-bottom-color: #2a3158;
        }
        html.dark .pricing-card__cycle { color: #cbd5e1; }
        html.dark .pricing-card__badge {
          color: #8ea1b8;
          background: #1a2140;
        }
        html.dark .pricing-card__badge--save {
          background: rgba(16, 185, 129, 0.18);
          color: #34d399;
        }
        html.dark .pricing-card__currency { color: #8ea1b8; }
        html.dark .pricing-card__input input { color: #f1f5f9; }
        html.dark .pricing-card__unit { color: #64748b; }

        html.dark .plan-modal__pricing-preview {
          background: #131833;
          border-color: #2a3158;
        }
        html.dark .plan-modal__pricing-preview h4 { color: #8ea1b8; }
        html.dark .pricing-preview__label { color: #64748b; }
        html.dark .pricing-preview__value { color: #f1f5f9; }
        html.dark .pricing-preview__save { color: #34d399; }

        html.dark .plan-modal__limit-item {
          border-color: #2a3158;
          background: #161c36;
        }
        html.dark .plan-modal__limit-item--unlimited {
          border-color: rgba(52, 211, 153, 0.5);
          background: rgba(16, 185, 129, 0.12);
        }
        html.dark .plan-modal__limit-item label { color: #cbd5e1; }
        html.dark .limit-input-row input {
          background: #0c1226;
          border-color: #2a3158;
          color: #e5e7eb;
        }
        html.dark .limit-input-row input:focus { border-color: #818cf8; }
        html.dark .limit-unlimited-display {
          color: #34d399;
          background: rgba(16, 185, 129, 0.18);
        }
        html.dark .limit-toggle {
          border-color: #2a3158;
          background: #0c1226;
          color: #8ea1b8;
        }
        html.dark .limit-toggle:hover { border-color: #818cf8; color: #a5b4fc; }
        html.dark .limit-toggle--active {
          background: #6366f1;
          border-color: #6366f1;
          color: #fff;
        }

        html.dark .feature-category {
          background: #131833;
          border-color: #2a3158;
        }
        html.dark .feature-category__header {
          background: #161c36;
          border-bottom-color: #2a3158;
        }
        html.dark .feature-category__title { color: #cbd5e1; }
        html.dark .feature-category__count {
          color: #8ea1b8;
          background: #1a2140;
        }
        html.dark .feature-toggle { color: #cbd5e1; }
        html.dark .feature-toggle__slider { background: #384070; }

        html.dark .plan-modal__actions {
          background: #131833;
          border-top-color: #2a3158;
        }
      `}</style>
    </div>
  );
};

export default PlansList;